/**
 * NodePalette Component
 *
 * Displays workflow nodes organized by category in an accordion layout.
 * Supports searching, filtering by target, and drag-and-drop to canvas.
 */

import React, { useEffect, useMemo } from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Search,
  AlertCircle,
  Play,
  GitBranch,
  Clock,
  Cloud,
  Bell,
  Database,
  BarChart3,
  Users,
  Cpu,
  Radio,
  Wifi,
  Monitor,
  MousePointer,
  // Node icons
  ArrowDownCircle,
  ArrowUpCircle,
  Activity,
  BarChart2,
  TrendingUp,
  Thermometer,
  Droplet,
  Sun,
  AlertTriangle,
  Link,
  Send,
  Download,
  XCircle,
  Headphones,
  Globe,
  Upload,
  Rss,
  Inbox,
  Layers,
  RefreshCw,
  CheckSquare,
  Circle,
  Volume2,
  UploadCloud,
  PlayCircle,
  StopCircle,
  GitMerge,
  List,
  Repeat,
  Shield,
  Box,
  Edit3,
  Eye,
  Calculator,
  Type,
  Sliders,
  FileText,
  Mail,
  MessageSquare,
  Zap,
  PlusCircle,
  Edit,
  Shuffle,
  UserCheck,
  CheckCircle,
  Calendar,
  ClipboardList,
  LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNodeRegistryStore, categoryLabels } from '@/stores/workflow/node-registry-store';
import { NodeCategory, NodeDefinitionSummary, ExecutionTarget } from '@/types/workflow-nodes';
import { TargetSelector } from './TargetSelector';

interface NodePaletteProps {
  className?: string;
  onNodeDragStart?: (node: NodeDefinitionSummary, event: React.DragEvent) => void;
  onNodeClick?: (node: NodeDefinitionSummary) => void;
  showTargetSelector?: boolean;
  defaultExpandedCategories?: NodeCategory[];
}

// Category icons mapping
const categoryIcons: Record<NodeCategory, React.ReactNode> = {
  [NodeCategory.CONTROL]: <Play className="h-4 w-4" />,
  [NodeCategory.VARIABLES]: <GitBranch className="h-4 w-4" />,
  [NodeCategory.TIMING]: <Clock className="h-4 w-4" />,
  [NodeCategory.INTEGRATIONS]: <Cloud className="h-4 w-4" />,
  [NodeCategory.NOTIFICATIONS]: <Bell className="h-4 w-4" />,
  [NodeCategory.DATABASE]: <Database className="h-4 w-4" />,
  [NodeCategory.ANALYTICS]: <BarChart3 className="h-4 w-4" />,
  [NodeCategory.HUMAN_WORKFLOW]: <Users className="h-4 w-4" />,
  [NodeCategory.GPIO]: <Cpu className="h-4 w-4" />,
  [NodeCategory.SENSORS]: <Radio className="h-4 w-4" />,
  [NodeCategory.COMMUNICATION]: <Wifi className="h-4 w-4" />,
  [NodeCategory.DISPLAY]: <Monitor className="h-4 w-4" />,
  [NodeCategory.INPUT]: <MousePointer className="h-4 w-4" />,
  [NodeCategory.SECURITY]: <Shield className="h-4 w-4" />,
};

// Node icon mapping (kebab-case to PascalCase Lucide components)
export const nodeIconMap: Record<string, LucideIcon> = {
  'arrow-down-circle': ArrowDownCircle,
  'arrow-up-circle': ArrowUpCircle,
  'activity': Activity,
  'bar-chart-2': BarChart2,
  'trending-up': TrendingUp,
  'thermometer': Thermometer,
  'droplet': Droplet,
  'sun': Sun,
  'radio': Radio,
  'alert-triangle': AlertTriangle,
  'link': Link,
  'send': Send,
  'download': Download,
  'x-circle': XCircle,
  'headphones': Headphones,
  'globe': Globe,
  'upload': Upload,
  'rss': Rss,
  'inbox': Inbox,
  'layers': Layers,
  'refresh-cw': RefreshCw,
  'check-square': CheckSquare,
  'circle': Circle,
  'clock': Clock,
  'volume-2': Volume2,
  'upload-cloud': UploadCloud,
  'play-circle': PlayCircle,
  'stop-circle': StopCircle,
  'git-branch': GitBranch,
  'git-merge': GitMerge,
  'list': List,
  'repeat': Repeat,
  'shield': Shield,
  'box': Box,
  'edit-3': Edit3,
  'eye': Eye,
  'calculator': Calculator,
  'type': Type,
  'sliders': Sliders,
  'file-text': FileText,
  'mail': Mail,
  'message-square': MessageSquare,
  'bell': Bell,
  'zap': Zap,
  'database': Database,
  'plus-circle': PlusCircle,
  'edit': Edit,
  'shuffle': Shuffle,
  'user-check': UserCheck,
  'check-circle': CheckCircle,
  'calendar': Calendar,
  'play': Play,
  'webhook': Zap, // Use Zap as fallback for webhook
  'clipboard-list': ClipboardList,
};

// Helper to get icon component from string name
const getNodeIcon = (iconName: string): React.ReactNode => {
  const IconComponent = nodeIconMap[iconName];
  if (IconComponent) {
    return <IconComponent className="h-4 w-4" />;
  }
  // Fallback to a default icon if not found
  return <Circle className="h-4 w-4" />;
};

// Node item component
interface NodeItemProps {
  node: NodeDefinitionSummary;
  onDragStart?: (node: NodeDefinitionSummary, event: React.DragEvent) => void;
  onClick?: (node: NodeDefinitionSummary) => void;
}

const NodeItem: React.FC<NodeItemProps> = ({ node, onDragStart, onClick }) => {
  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.setData('application/json', JSON.stringify(node));
    e.dataTransfer.effectAllowed = 'copy';
    onDragStart?.(node, e);
  };

  const handleClick = () => {
    onClick?.(node);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick?.(node);
    }
  };

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      className={cn(
        'flex items-center gap-2 p-2 rounded-md cursor-grab',
        'hover:bg-accent/50 active:cursor-grabbing transition-colors',
        'border border-transparent hover:border-border',
        'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
        node.deprecated && 'opacity-50'
      )}
      style={{ borderLeftColor: node.color, borderLeftWidth: 3 }}
      title={node.description}
      aria-label={`${node.name} node. ${node.description}`}
    >
      <span className="flex-shrink-0" role="img" aria-hidden="true">
        {getNodeIcon(node.icon)}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{node.name}</p>
        <p className="text-xs text-muted-foreground truncate">{node.description}</p>
      </div>
      {node.deprecated && (
        <Badge variant="outline" className="text-xs shrink-0">
          Deprecated
        </Badge>
      )}
      {node.targets.includes(ExecutionTarget.ESP32) &&
        !node.targets.includes(ExecutionTarget.ALL) && (
          <Badge variant="secondary" className="text-xs shrink-0">
            <Cpu className="h-3 w-3 mr-1" />
            ESP32
          </Badge>
        )}
    </div>
  );
};

// Loading skeleton
const NodePaletteSkeleton: React.FC = () => (
  <div className="space-y-4 p-4">
    <Skeleton className="h-10 w-full" />
    <Skeleton className="h-8 w-full" />
    <div className="space-y-2">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <div className="pl-4 space-y-1">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Error state
const NodePaletteError: React.FC<{ error: string; onRetry: () => void }> = ({
  error,
  onRetry,
}) => (
  <div className="flex flex-col items-center justify-center p-8 text-center">
    <AlertCircle className="h-12 w-12 text-destructive mb-4" />
    <h3 className="text-lg font-semibold mb-2">Failed to load nodes</h3>
    <p className="text-sm text-muted-foreground mb-4">{error}</p>
    <button
      onClick={onRetry}
      className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
    >
      Retry
    </button>
  </div>
);

// Empty state
const NodePaletteEmpty: React.FC<{ searchQuery: string }> = ({ searchQuery }) => (
  <div className="flex flex-col items-center justify-center p-8 text-center">
    <Search className="h-12 w-12 text-muted-foreground mb-4" />
    <h3 className="text-lg font-semibold mb-2">No nodes found</h3>
    <p className="text-sm text-muted-foreground">
      {searchQuery
        ? `No nodes match "${searchQuery}". Try a different search term.`
        : 'No nodes available for the selected target.'}
    </p>
  </div>
);

export const NodePalette: React.FC<NodePaletteProps> = ({
  className,
  onNodeDragStart,
  onNodeClick,
  showTargetSelector = true,
  defaultExpandedCategories = [NodeCategory.CONTROL],
}) => {
  const {
    initialize,
    getFilteredNodesByCategory,
    searchQuery,
    setSearchQuery,
    isLoading,
    isInitialized,
    error,
    stats,
  } = useNodeRegistryStore();

  // Initialize on mount
  useEffect(() => {
    initialize();
  }, [initialize]);

  // Get filtered nodes
  const nodesByCategory = useMemo(
    () => getFilteredNodesByCategory(),
    [getFilteredNodesByCategory]
  );

  // Default expanded categories
  const defaultExpanded = useMemo(
    () => defaultExpandedCategories.map((c) => c.toString()),
    [defaultExpandedCategories]
  );

  // Handle search input
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  // Handle retry
  const handleRetry = () => {
    useNodeRegistryStore.setState({ isInitialized: false });
    initialize();
  };

  // Loading state
  if (isLoading && !isInitialized) {
    return <NodePaletteSkeleton />;
  }

  // Error state
  if (error && !isInitialized) {
    return <NodePaletteError error={error} onRetry={handleRetry} />;
  }

  return (
    <div className={cn('flex flex-col h-full', className)}>
      {/* Header with target selector */}
      <div className="p-4 border-b space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Nodes</h2>
          {stats && (
            <Badge variant="outline" className="text-xs">
              {stats.total} total
            </Badge>
          )}
        </div>

        {showTargetSelector && <TargetSelector size="sm" />}

        {/* Search input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search nodes..."
            value={searchQuery}
            onChange={handleSearchChange}
            className="pl-9"
            aria-label="Search nodes"
          />
        </div>
      </div>

      {/* Node list */}
      <ScrollArea className="flex-1">
        {nodesByCategory.length === 0 ? (
          <NodePaletteEmpty searchQuery={searchQuery} />
        ) : (
          <Accordion
            type="multiple"
            defaultValue={defaultExpanded}
            className="w-full"
          >
            {nodesByCategory.map((category) => (
              <AccordionItem
                key={category.category}
                value={category.category}
                className="border-none"
              >
                <AccordionTrigger className="px-4 py-2 hover:no-underline hover:bg-accent/50">
                  <div className="flex items-center gap-2">
                    {categoryIcons[category.category]}
                    <span className="font-medium">{category.label}</span>
                    <Badge variant="secondary" className="text-xs ml-auto mr-2">
                      {category.nodes.length}
                    </Badge>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-2 pb-2">
                  <div className="space-y-1">
                    {category.nodes.map((node) => (
                      <NodeItem
                        key={node.type}
                        node={node}
                        onDragStart={onNodeDragStart}
                        onClick={onNodeClick}
                      />
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </ScrollArea>
    </div>
  );
};

export default NodePalette;
